import type { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import BodyText from 'LuxKit/Typography/BodyText'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummaryImage from './Common/LesaPurchaseSummaryImage'

interface Props {
  order: App.Order
  /** Product-level tag: "Room 2", "Experience", "Flight". */
  label: string
  title: string
  subtitle?: ReactNode
  /** Occupancy, dates, route — the one line of context under the title. */
  detail?: ReactNode
  /** Leading icon for `detail`, matching the icon LesaHotelItemSummary puts on the same line. */
  detailIcon?: ReactNode
  imageId?: string
  bookingNumber?: string
  onSelect: () => void
}

/**
 * The shared frame for one selectable item of a booking, in the chat's card style. Flash
 * accommodation keeps its own richer card (LesaHotelItemSummary, which adds inclusions and its own
 * select payload); this covers the product types that had no item-level card at all — bedbank
 * items, bedbank rooms, experiences and flights.
 *
 * Layout deliberately mirrors LesaHotelItemSummary element for element: same container, same
 * header, text column on the left and a thumbnail-above-button column on the right. The right-hand
 * column is what sets the card's proportions, so it is rendered even for a product with no image
 * to give — otherwise the button climbs up beside the text and the card comes out visibly shorter
 * and wider than the flash one sitting next to it in the same list.
 */
export default function LesaSelectableItemCard(props: Props) {
  const { order, label, title, subtitle, detail, detailIcon, imageId, bookingNumber, onSelect } = props
  const intl = useIntl()

  return (
    <LesaPurchaseSummaryContainer>
      <LesaPurchaseSummaryHeader order={order}>
        <StructureLabel variant="standard">{label}</StructureLabel>
      </LesaPurchaseSummaryHeader>
      <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
        <Group direction="vertical" gap={4}>
          <BodyText variant="medium" weight="bold">{title}</BodyText>
          {!!subtitle && <BodyText variant="medium">{subtitle}</BodyText>}
          {!!bookingNumber && (
            <BodyText variant="small" colour="light-foreground-alt">
              <strong>
                {intl.formatMessage({
                  id: 'YjLVoUox1wvaPox9aFkk',
                  defaultMessage: 'Booking ID:',
                  description: 'Label for hotel item booking ID on the hotel or room booking page',
                })}
              </strong> {bookingNumber}
            </BodyText>
          )}
          {!!detail && (
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {detailIcon}
              <BodyText variant="medium">{detail}</BodyText>
            </Group>
          )}
        </Group>
        <Group gap={16} direction="vertical">
          <LesaPurchaseSummaryImage id={imageId} />
          <TextButton onClick={onSelect} fit="mobile-full-width" size="small" kind="secondary">
            {intl.formatMessage({
              id: 'HxLoKDQxg3hlDticRjzn',
              defaultMessage: 'Select',
              description: 'Button to select a hotel room item on the hotel or room booking page',
            })}
          </TextButton>
        </Group>
      </Group>
    </LesaPurchaseSummaryContainer>
  )
}
